/**
 * Maps GraphQL billing account response to TypeScript BillingAccountInfo interface.
 *
 * GraphQL schema uses snake_case (credit_balance, last4digits)
 * TypeScript interface uses camelCase (creditBalance, last4)
 */
import type { BillingAccountInfo } from '../types';

/**
 * Raw billing account shape from GraphQL responses.
 * This matches the shape returned by verifyPasswordlessOTP, linkPhoneToExistingAccount, etc.
 * Note: cents may come as string or number depending on the query source.
 */
export interface GraphQLBillingAccount {
  id?: string;
  type?: string;
  credit_balance?: {
    cents: number | string;
    currency?: string | null;
  } | null;
  default_card?: {
    brand?: string | null;
    last4digits?: string | null;
  } | null;
}

/**
 * Maps a GraphQL billing account response to the BillingAccountInfo interface.
 *
 * @param raw - The raw billing account from GraphQL (snake_case fields)
 * @returns BillingAccountInfo with camelCase fields, or null if input is null/undefined
 */
export function mapBillingAccount(
  raw: GraphQLBillingAccount | null | undefined
): BillingAccountInfo | null {
  if (!raw) return null;

  // Parse cents - may be string or number depending on query source
  const parseCents = (cents: number | string | undefined): number => {
    if (cents === undefined) return 0;
    return typeof cents === 'string' ? parseInt(cents, 10) : cents;
  };

  return {
    paymentType: raw.type === 'prepaid' ? 'prepaid' : 'postpaid',
    creditBalance: raw.credit_balance
      ? {
          amount: parseCents(raw.credit_balance.cents) / 100,
          currency: raw.credit_balance.currency ?? 'USD',
        }
      : undefined,
    // Only create defaultCard if both brand and last4 are present
    defaultCard:
      raw.default_card?.brand && raw.default_card?.last4digits
        ? {
            brand: raw.default_card.brand,
            last4: raw.default_card.last4digits,
          }
        : undefined,
  };
}
